import { CategoryId, PartialExceptionExpanded } from '../../../lib/categories/categoryTypes';
import {
  forcedTitleAsCategory,
  getExceptionsBy,
  keyGetterFieldType,
  renderer,
} from '../../../screens/Modals/CategoryExceptionsModal/categoryExceptionsModalLib';
import { isRootCategory } from '../../../lib/categories/categoriesLib';
import { Column, Title } from '@ydistri/ds';
import { ColoredHeadlineRow } from '../../../screens/Modals/CategoryExceptionsModal/CategoryExceptionsModal';
import ResetFormField from '../../../screens/Modals/CategoryExceptionsModal/ResetFormField';
import { List } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useGetCategoryConfigurationDefinitionsQuery } from '../../../apis/apiApplication';
import { sortConfigurationFieldTypes } from '../../../apis/applicationLib';
import { useExceptions } from '../../../hooks/useExceptions';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import {
  getDefinitionByFieldType,
  getSectionTitleFromDefinition,
} from '../../../lib/configuration/configurationLib';

interface ExceptionsByCategoryListingProps {
  categoryId: CategoryId;
}

const ExceptionsByCategoryListing: React.FC<ExceptionsByCategoryListingProps> = ({
  categoryId,
}) => {
  const exceptions = useExceptions(categoryId);
  const { data: configurationDefinitions } = useGetCategoryConfigurationDefinitionsQuery();
  const isConfigurationEditable = useIsConfigurationEditable();

  const sortedFieldTypes = useMemo(
    () => sortConfigurationFieldTypes(configurationDefinitions ?? []),
    [configurationDefinitions],
  );

  const categorySorter = (a: PartialExceptionExpanded, b: PartialExceptionExpanded) => {
    return a.titleFull.localeCompare(b.titleFull);
  };

  const exceptionsByField = useMemo(() => {
    return getExceptionsBy(exceptions, keyGetterFieldType, categorySorter);
  }, [exceptions]);

  const listItemRenderer = useCallback(
    (exceptionExpanded: PartialExceptionExpanded) =>
      renderer(
        exceptionExpanded,
        forcedTitleAsCategory,
        configurationDefinitions ?? [],
        undefined,
        false,
        !isConfigurationEditable,
      ),
    [configurationDefinitions, isConfigurationEditable],
  );

  return sortedFieldTypes.arr.map(configurationField => {
    if ((exceptionsByField[configurationField]?.length ?? 0) === 0) return null;

    const definition = getDefinitionByFieldType(configurationField, configurationDefinitions ?? []);
    if (!definition) return null;

    const filteredExceptions = exceptionsByField[configurationField]?.filter(
      e => !isRootCategory(e.category.id),
    );
    if (filteredExceptions?.length === 0) return null;

    return (
      <Column key={definition.id}>
        <ColoredHeadlineRow $justifyContent="space-between">
          <Title $size="small" $marginTop="small" $marginBottom="none">
            {getSectionTitleFromDefinition(definition)}
            {definition.title}
          </Title>
          <ResetFormField definition={definition} exceptionsByField={filteredExceptions ?? []} />
        </ColoredHeadlineRow>
        <List
          dataSource={filteredExceptions}
          loading={false}
          size="small"
          renderItem={listItemRenderer}
          data-testid={`ConfigurationGroupExceptions-${configurationField}`}
        />
      </Column>
    );
  });
};

export default ExceptionsByCategoryListing;
